import { useMemo } from 'react';
import {
	getParentWorktype,
	isDerivedFrom,
	isEntryOf,
	isManifestation,
	TitleFull as Title,
} from '@warehouse/title/core';
import {
	compilationUuid,
	editUuid,
	getWorkTypeObject,
	getWorkTypeUuid,
	manifestationUuid,
} from '@warehouse/title/infra';
import { WorkType } from '@warehouse/title-shared/core';
import { ChildrenRelationships } from 'libs/title/core/children-relationship';
import useWorkTypes, { getNameByUuid } from '../../../utils/hooks/titles/useWorkTypes';
import dynamicJson from '../../../assets/json-administration-profiles/dynamicTabsToDisplay.json';
import { useSearchRelationshipChildren } from '../../../../libs/title/domain/useSearchRelationshipChildren';

export type ChildrenTabNameButtonId =
	| 'add-single'
	| 'add-multiple'
	| 'add-existing'
	| 'create-multiple-manifestations'
	| 'create-multiple-edits';

export interface ChildTabDefinition {
	childWorkTypeUuid: string;
	singularLabel: string;
	pluralLabel: string;
	relationshipType: string;
	addChildButtonOptions: ChildrenTabNameButtonId[];
}

export function useChildrenTabs(title: Title | undefined): ChildTabDefinition[] {
	const { getWorkType } = useWorkTypes();
	const currentWorkType = title?.readonly?.workType;
	const parentAbstractionWorktype = title?.readonly?.parentAbstractionWorkType;
	const parentWorkType = getParentWorktype(title?.readonly);
	const parentIsEdit = parentWorkType === getNameByUuid(editUuid());
	const dynamicTabsConfig = useMemo(
		() => dynamicJson.find((wt) => 'dynamicTabsToDisplay' in wt)?.dynamicTabsToDisplay ?? [],
		[],
	);
	const relationships = useSearchRelationshipChildren(title?.uuid);

	return useMemo(() => {
		if (!currentWorkType) return [];
		if (isManifestation(title?.readonly) && parentIsEdit) {
			return [];
		}
		const baseWorkType =
			currentWorkType === getNameByUuid(manifestationUuid()) && parentAbstractionWorktype
				? parentAbstractionWorktype
				: currentWorkType;

		const workType = getWorkType(getWorkTypeUuid(baseWorkType));
		if (!workType) return [];
		const staticTabs = workType.tabsToDisplay.map((tab) => {
			const computeRelationshipType = () => {
				if (workType?.value === compilationUuid()) {
					if (tab.value === getWorkTypeObject(WorkType.Manifestation).uuid) {
						return isDerivedFrom();
					}
					return isEntryOf();
				}
				return tab.relationshipRequired;
			};

			return {
				childWorkTypeUuid: tab.value,
				singularLabel: tab.labelSingular,
				pluralLabel: tab.labelPlural,
				relationshipType: computeRelationshipType(),
				addChildButtonOptions: tab.addChildButtonOptions as ChildrenTabNameButtonId[],
			};
		});

		const staticTabWorkTypeUuids = new Set(workType.tabsToDisplay.map((tab) => tab.value));

		const children = relationships.data;
		const dynamicTabs: ChildTabDefinition[] = dynamicTabsConfig.flatMap((tab) => {
			if (staticTabWorkTypeUuids.has(tab.value)) return [];

			const key = tab.labelPlural.toLowerCase() as keyof ChildrenRelationships;

			if (!children?.[key]) return [];

			return [
				{
					childWorkTypeUuid: tab.value,
					singularLabel: tab.labelSingular,
					pluralLabel: tab.labelPlural,
					relationshipType: tab.relationshipRequired!,
					addChildButtonOptions: tab.addChildButtonOptions as ChildrenTabNameButtonId[],
				},
			];
		});

		return [...staticTabs, ...dynamicTabs];
	}, [
		currentWorkType,
		parentAbstractionWorktype,
		getWorkType,
		relationships.data,
		dynamicTabsConfig,
		parentIsEdit,
		title?.readonly,
	]);
}
